import axios from '../globalAxios';
import { basketLine } from '../../models/Basket';
import { appConfig } from '../../config';

import { uniqBy } from 'lodash'

const baseUrl = appConfig.api.baseUrl;

export default class OrderApi {
    static getOrder = async (orderId: number) => {
        const url = baseUrl + 'Order/GetOrder';
        return await axios.post(url, null, {params: { apporderid: orderId }});
    }

    /** Applies server-side delivery date correction before returning order (dashboard calendar). */
    static getOrder1 = async (orderId: number) => {
        const url = baseUrl + 'Order/GetOrder1';
        return await axios.post(url, null, {params: { apporderid: orderId }});
    }

    static getOrderRevision = async (orderId: number) => {
        const url = baseUrl + 'Order/GetOrderRevision';
        return await axios.post(url, null, {params: { apporderid: orderId }});
    }

    static getOrderRevisionLog = async (orderId: number, sinceRevision?: number, take: number = 25) => {
        const url = baseUrl + 'Order/GetOrderRevisionLog';
        const params: any = { apporderid: orderId, take };

        if (sinceRevision != null) {
            params.sinceRevision = sinceRevision;
        }

        return await axios.post(url, null, { params });
    }

    static getCustomerOrderActivitySummary = async (
        skip: number = 0,
        take: number = 25,
        state: 'all' | 'open' | 'closed' = 'all',
        search?: string
    ) => {
        const url = baseUrl + 'Order/GetCustomerOrderActivitySummary';
        const params: any = { skip, take, state };

        if (search && search.trim()) {
            params.search = search.trim();
        }

        return await axios.post(url, null, { params });
    }

    static getOrderFromLocalStorage = () => {
        const orderId = localStorage.getItem('order');

        if (orderId) {
            return +orderId;
        }

        return orderId;
    }

    static getRecentOrdersFromLocalStorage = () => {
        const recentClosedOrdersLocalStorage = localStorage.getItem('recentClosedOrders')
        let localStorageClosedOrders = [];
        let recentClosedOrders = []

        if (recentClosedOrdersLocalStorage) {
            localStorageClosedOrders = JSON.parse(recentClosedOrdersLocalStorage);
        }

        recentClosedOrders = uniqBy(localStorageClosedOrders, 'appOrderId')

        return recentClosedOrders;
    }

    static removeRecentOrdersFromLocalStorage = (appOrderId: number) => {
        const recentClosedOrdersLocalStorage = localStorage.getItem('recentClosedOrders')
        let localStorageClosedOrders = [];
        let recentClosedOrders = []

        if (recentClosedOrdersLocalStorage) {
            localStorageClosedOrders = JSON.parse(recentClosedOrdersLocalStorage).filter((order: any) => order.appOrderId !== appOrderId)
        }

        recentClosedOrders = uniqBy(localStorageClosedOrders, 'appOrderId');

        localStorage.setItem('recentClosedOrders', JSON.stringify(recentClosedOrders))

        return recentClosedOrders;
    }

    static getOpenOrders = async () => {
        const url = baseUrl + 'Order/GetMyOpenOrders';
        return await axios.post(url);
    }

    static getOpenOrderBasketHints = async (items?: Array<{ master: string; color: string }>) => {
        const url = baseUrl + 'Order/GetOpenOrderBasketHints';
        const body =
            items && items.length
                ? { items: items.map((i) => ({ master: i.master, color: i.color })) }
                : null;
        return axios.post(url, body);
    }

    static getClosedOrders = async () => {
        const url = baseUrl + 'Order/GetMyClosedOrders';
        return await axios.post(url);
    }

    static getShops = async () => {
        const url = baseUrl + 'Order/MyShops';
        return await axios.post(url);
    }

    static createOrder = async (orderType: string, customerId: number) => {
        const url = baseUrl + 'Order/CreateNewOrder';
        return await axios.post(url, null, {params: { orderType, customerid: customerId }});
    };

    static applyChanges = async (orderId: number, changes: basketLine, expectedRevision: number) => {
        const lines = Object.keys(changes).map((itemId) => ({
            itemid: itemId,
            quantity: changes[itemId],
        }));

        if (!lines.length) {
            return;
        }

        const url = baseUrl + 'Order/AddItemsToOrder/' + orderId;
        return await axios.post(url, {
            expectedRevision,
            lines,
        });
    }
    
    static updateOrder = async (data: any) => {
        const url = baseUrl + 'Order/UpdateOrder';
        return axios.patch(url, data);
    }

    static applyOrder = async (orderId: number) => {
        const url = baseUrl + 'Order/ApplyOrder';
        return axios.post(url, null, {params: { apporderid: orderId }});
    }

    static deleteOrder = async (orderId: number) => {
        const url = baseUrl + 'Order/DeleteOpenOrder';
        return axios.post(url, null, {params: { apporderid: orderId }});
    }

    static peakNavOrders = async (customerId: number) => {
        const url = baseUrl + 'Order/PeakNavOrders';
        return axios.post(url, null, {params: { customerid: customerId }});
    }

    static peakNavOrdersNew = async (customerId: number, final: boolean, allshops: boolean = false) => {
        const url = baseUrl + 'Order/PeakNavOrders';

        interface IParams {
            customerid: number
            final?: boolean
            allshops: boolean
        }

        let params: IParams = {
            customerid: customerId,
            allshops: allshops,
        }

        if (final !== undefined) {
            params['final'] = final;
        }

        return axios.post(url, null, { params });
    }

    static peakNavCustomerShipments = async (customerId: number, allShops: boolean = false) => {
        const url = baseUrl + 'Order/PeakNavCustomerShipments';
        return axios.post(url, null, {params: { customerid: customerId, allShops: allShops}});
    }

    // /api/Order/PeakNavOrderUnshippedShipments
    static peakNavOrderUnshippedShipments = async (sonumber: string) => {
        const url = baseUrl + 'Order/PeakNavOrderUnshippedShipments';
        return axios.post(url, null, {params: { sonumber: sonumber }});
    }

    // /api/Order/PeakNavShipmentItems
    static peakNavShipmentItems = async (bsl: string) => {
        const url = baseUrl + 'Order/PeakNavShipmentItems';
        return axios.post(url, null, {params: { bsl: bsl }});
    }

    // PeakNavOrderShipments
    static peakNavOrderShipments = async (sonumber: string) => {
        const url = baseUrl + 'Order/PeakNavOrderShipments';
        return axios.post(url, null, {params: { sonumber: sonumber }});
    }

    // /api/Order/PeakNavOrderUnshippedItems
    static peakNavOrderUnshippedItems = async (sonumber: string, deliverycode: string) => {
        const url = baseUrl + 'Order/PeakNavOrderUnshippedItems';
        return axios.post(url, null, {params: { SO: sonumber, deliverycode: deliverycode }});
    }

    static lookupOrderInNav = async (apporderid: number) => {
        const url = baseUrl + 'Order/LookupOrderInNav';
        return axios.post(url, null, {params: { apporderid }});
    }

    static openNavOrder = async (navOrderId: number) => {
        const url = baseUrl + 'Order/ImportNavOrder';
        return axios.post(url, null, {params: { navorderid: navOrderId }});
    }

    // /api/OrderTypes/GetSeasonOrderTypes/{seasoncode}
    static getSeasonOrderTypes = async (seasoncode: string) => {
        const url = baseUrl + `OrderTypes/GetSeasonOrderTypes/${seasoncode}`;
        return axios.post(url);
    }

    /** Allowed season codes for an order type (Model.OrderTypeSeasons). */
    static getOrderTypeSeasons = async (orderType: string) => {
        const encoded = encodeURIComponent(orderType);
        const url = baseUrl + `OrderTypes/GetOrderTypeSeasons/${encoded}`;
        return axios.post<string[]>(url);
    }

    // GetItemOrderTypes — Model.OrderTypesForMasterColor (shop, search, dashboard)
    static getItemOrderTypes = async (master: string, color: string) => {
        const url = baseUrl + `OrderTypes/GetItemOrderTypes/${master}/${color}`;
        return axios.post(url);
    }

    /** Wishlist overlay only — Model.AllOrderTypesForMasterColor */
    static getAllItemOrderTypes = async (master: string, color: string) => {
        const url = baseUrl + `OrderTypes/GetAllItemOrderTypes/${master}/${color}`;
        return axios.post(url);
    }

    static getCustomerShippingAddresses = async (customerId: number) => {
        const url = baseUrl + 'Order/GetCustomerShippingAddresses';
        return axios.post(url, null, {params: { customerid: customerId }});
    }

    static moveOrderDelivery = async (
        orderId: number,
        deliveryCode: string,
        newDate: string,
        expectedRevision?: number | null
    ) => {
        const url = baseUrl + 'Order/MoveOrderDelivery';
        const params: any = {
            apporderid: orderId,
            srcDeliveryCode: deliveryCode,
            newDate,
        };

        if (expectedRevision != null) {
            params.expectedRevision = expectedRevision;
        }

        return axios.post(url, null, { params })
    }

    static getCountryRegionCodes = async () => {
        const url = baseUrl + 'Order/AllCountryRegionCodes';
        return axios.post(url);
    }

    static ensureOrderStockChanges = async (
        orderId: number,
        confirmChange: boolean = false,
        expectedRevision?: number | null
    )  => {
        const params: any = {
            apporderid: orderId,
            changeOrderQuantity: confirmChange,
        };

        if (confirmChange && expectedRevision != null) {
            params.expectedRevision = expectedRevision;
        }

        const url = baseUrl + 'Order/EnsureOrderStockChanges';
        return axios.post(url, null, { params });
    }

    // /api/Order/GetMoloItemsOnOrder
    static GetMoloItemsOnOrder = async (apporderid: number) => {
        const url = baseUrl + 'Order/GetMoloItemsOnOrder';
        return axios.post(url, null, {params: { apporderid }});
    }

    static getCustomerDeliveryDownloadInfo = async (customerno: string) => {
        const url = baseUrl + 'Order/GetCustomerDeliveryDownloadInfo/' + customerno;
        return await axios.post(url);
    }

    // /api/Order/GetOrderDeliveries
    static getOrderDeliveries = async (appOrderId: number) => {
        const url = baseUrl + 'Order/GetOrderDeliveries';
        return axios.post(url, null, {params: { appOrderId }});
    }

    static getGotIt = () => {
        const storageVal = localStorage.getItem('gotIt') || 'false';

        return localStorage.getItem('gotIt') === null ? false : JSON.parse(storageVal)
    }

    // GetFilteredOrderLines
    static getFilteredOrderLines = async (appOrderId: number, deliverycode: string, seasoncode?: string) => {
        const url = baseUrl + 'Order/GetFilteredOrderLines';
        // return axios.post(url, null, {params: { appOrderId, deliverycode, seasoncode}});
        return axios.post(url, { appOrderId, deliverycode})
    }

    static getAllClosedOrders = async (customerId: string | number, allShops: boolean = false) => {
        const requests: any[] = [
            OrderApi.peakNavOrdersNew(+customerId, true, allShops),
            OrderApi.peakNavCustomerShipments(+customerId, allShops)
        ]
        const responses = await axios.all(requests);
        
        const peakNavordersResponse = responses[0]
        const shipmentsResponse = responses[1]

        const allOrders: any[] = [ ...peakNavordersResponse.data , ...shipmentsResponse.data.map((item: any) => {
            item.isFullyShipped = true
            return item
        }) ]

        return allOrders;
    }

    static getAllOpenOrders = async (customerId: string | number, allShops: boolean = false) => {
        const requests: any[] = [
            OrderApi.peakNavOrdersNew(+customerId, false, allShops),
            OrderApi.getOpenOrders()
        ]

        const responses = await axios.all(requests);
        const peakNavordersResponse = responses[0].data.map((res: any) => {
            let sellToCustomerName = res.shipttoname || '';

            return {
                appOrderId: null,
                sellToCustomerNo: res.selltocustomerno,
                sellToCustomerName: sellToCustomerName,
                sellToAddress: null,
                sellToCity: null,
                sellToCountry_RegionCode: null,
                shipttoname: '',
                orderType: res.ordertype,
                salespersonnote: res.salespersonnote,
                navorderid: res.navorderid,
                status: 'Submitted',
            }
        })
        const openOrdersResponse = responses[1]

        const allOrders: any[] = [
            ...peakNavordersResponse ,
            ...openOrdersResponse.data.map((item: any) => {
                item.status = 'Open'
                return item
            })
        ];

        return allOrders;
    }

    // PeakShipmentTracking
    static PeakShipmentTracking = (bsl: string) => {
        const url = baseUrl + 'Order/PeakShipmentTracking'
        return axios.post(url, null, {
            params: {
                bsl
            }
        })
    }

    static PeakOrderTrackingCodes = (sonumber: string) => {
        const url = baseUrl + 'Order/PeakOrderTrackingCodes'
        return axios.post(url, null, {
            params: {
                sonumber
            }
        })
    }

    static fetchTrackingLinksForDrop = async (order: any, drop: any) => {
        const bsl = drop && drop.bsl ? String(drop.bsl).trim() : '';
        const sonumber = order && order.navorderid ? String(order.navorderid).trim() : '';

        if (bsl) {
            const response = await OrderApi.PeakShipmentTracking(bsl);
            return {
                links: Array.isArray(response.data) ? response.data : [],
                title: `Tracking — ${bsl}`,
            };
        }

        if (sonumber) {
            const response = await OrderApi.PeakOrderTrackingCodes(sonumber);
            return {
                links: Array.isArray(response.data) ? response.data : [],
                title: `Tracking — order ${sonumber}`,
            };
        }

        return {
            links: [],
            title: 'Tracking links',
        };
    }
}
